// src/store/thunks/karigar/paymentThunks.js
// ============================================================================
// KARIGAR PAYMENT THUNKS - UPDATED VERSION
// ============================================================================
// Changes:
// 1. Added cancelPayment thunk
// 2. Better organized sections
// 3. Consistent error handling
// ============================================================================

import { createAsyncThunk } from "@reduxjs/toolkit";
import api from "../../../services/api";

const BASE_URL = "/admin-karigar/payments";

// ============================================================================
// FETCH / LIST PAYMENTS
// ============================================================================

/**
 * Fetch all payments with filters
 * GET /api/admin-karigar/payments
 */
export const fetchPayments = createAsyncThunk(
  "karigar/payment/fetchAll",
  async (params = {}, { rejectWithValue }) => {
    try {
      const response = await api.get(BASE_URL, { params });
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data || { message: error.message }
      );
    }
  }
);

/**
 * Fetch payment by ID
 * GET /api/admin-karigar/payments/:paymentId
 */
export const fetchPaymentById = createAsyncThunk(
  "karigar/payment/fetchById",
  async (paymentId, { rejectWithValue }) => {
    try {
      const response = await api.get(`${BASE_URL}/${paymentId}`);
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data || { message: error.message }
      );
    }
  }
);

/**
 * Fetch payments by karigar
 * GET /api/admin-karigar/payments/karigar/:karigarId
 */
export const fetchPaymentsByKarigar = createAsyncThunk(
  "karigar/payment/fetchByKarigar",
  async ({ karigarId, params = {} }, { rejectWithValue }) => {
    try {
      const response = await api.get(`${BASE_URL}/karigar/${karigarId}`, {
        params,
      });
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data || { message: error.message }
      );
    }
  }
);

/**
 * Fetch payments by job
 * GET /api/admin-karigar/payments/job/:jobId
 */
export const fetchPaymentsByJob = createAsyncThunk(
  "karigar/payment/fetchByJob",
  async (jobId, { rejectWithValue }) => {
    try {
      const response = await api.get(`${BASE_URL}/job/${jobId}`);
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data || { message: error.message }
      );
    }
  }
);

/**
 * Fetch pending payments
 * GET /api/admin-karigar/payments/pending
 */
export const fetchPendingPayments = createAsyncThunk(
  "karigar/payment/fetchPending",
  async (params = {}, { rejectWithValue }) => {
    try {
      const response = await api.get(`${BASE_URL}/pending`, { params });
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data || { message: error.message }
      );
    }
  }
);

// ============================================================================
// CREATE PAYMENTS
// ============================================================================

/**
 * Create payment for karigar (from completed jobs)
 * POST /api/admin-karigar/payments
 *
 * Note: This creates a PENDING payment. Use processPayment to complete it.
 * The job's paidAmount is NOT updated until processPayment is called.
 */
export const createPayment = createAsyncThunk(
  "karigar/payment/create",
  async (paymentData, { rejectWithValue }) => {
    try {
      const response = await api.post(BASE_URL, paymentData);
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data || { message: error.message }
      );
    }
  }
);

/**
 * Create advance payment
 * POST /api/admin-karigar/payments/advance
 */
export const createAdvancePayment = createAsyncThunk(
  "karigar/payment/createAdvance",
  async (
    { karigarId, amount, notes, branchCode, branchName },
    { rejectWithValue }
  ) => {
    try {
      const response = await api.post(`${BASE_URL}/advance`, {
        karigarId,
        amount,
        notes,
        branchCode,
        branchName,
      });
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data || { message: error.message }
      );
    }
  }
);

/**
 * Create deduction record
 * POST /api/admin-karigar/payments/deduction
 */
export const createDeduction = createAsyncThunk(
  "karigar/payment/createDeduction",
  async (
    { karigarId, amount, type, description, notes, branchCode },
    { rejectWithValue }
  ) => {
    try {
      const response = await api.post(`${BASE_URL}/deduction`, {
        karigarId,
        amount,
        type,
        description,
        notes,
        branchCode,
      });
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data || { message: error.message }
      );
    }
  }
);

/**
 * Generate payments for a period (settlement)
 * POST /api/admin-karigar/payments/generate
 */
export const generatePaymentsForPeriod = createAsyncThunk(
  "karigar/payment/generateForPeriod",
  async ({ periodStart, periodEnd, branchCode }, { rejectWithValue }) => {
    try {
      const response = await api.post(`${BASE_URL}/generate`, {
        periodStart,
        periodEnd,
        branchCode,
      });
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data || { message: error.message }
      );
    }
  }
);

// ============================================================================
// UPDATE PAYMENT
// ============================================================================

/**
 * Update payment (only pending payments can be updated)
 * PUT /api/admin-karigar/payments/:paymentId
 */
export const updatePayment = createAsyncThunk(
  "karigar/payment/update",
  async ({ paymentId, data }, { rejectWithValue }) => {
    try {
      const response = await api.put(`${BASE_URL}/${paymentId}`, data);
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data || { message: error.message }
      );
    }
  }
);

// ============================================================================
// PAYMENT ACTIONS (Status Changes)
// ============================================================================

/**
 * Approve payment
 * POST /api/admin-karigar/payments/:paymentId/approve
 */
export const approvePayment = createAsyncThunk(
  "karigar/payment/approve",
  async ({ paymentId, notes }, { rejectWithValue }) => {
    try {
      const response = await api.post(`${BASE_URL}/${paymentId}/approve`, {
        notes,
      });
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data || { message: error.message }
      );
    }
  }
);

/**
 * Reject payment
 * POST /api/admin-karigar/payments/:paymentId/reject
 */
export const rejectPayment = createAsyncThunk(
  "karigar/payment/reject",
  async ({ paymentId, reason }, { rejectWithValue }) => {
    try {
      const response = await api.post(`${BASE_URL}/${paymentId}/reject`, {
        reason,
      });
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data || { message: error.message }
      );
    }
  }
);

/**
 * Process payment (mark as completed/paid)
 * POST /api/admin-karigar/payments/:paymentId/process
 *
 * IMPORTANT: This is where the actual payment happens:
 * - Job's paidAmount is updated
 * - Karigar's totalEarnings is updated
 * - Karigar's pendingPayments is decremented
 * - Receipt is generated
 */
export const processPayment = createAsyncThunk(
  "karigar/payment/process",
  async (
    { paymentId, paymentMethod, transactionId, referenceNumber, notes },
    { rejectWithValue }
  ) => {
    try {
      const response = await api.post(`${BASE_URL}/${paymentId}/process`, {
        paymentMethod,
        transactionId,
        referenceNumber,
        notes,
      });
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data || { message: error.message }
      );
    }
  }
);

/**
 * Hold payment
 * POST /api/admin-karigar/payments/:paymentId/hold
 */
export const holdPayment = createAsyncThunk(
  "karigar/payment/hold",
  async ({ paymentId, reason }, { rejectWithValue }) => {
    try {
      const response = await api.post(`${BASE_URL}/${paymentId}/hold`, {
        reason,
      });
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data || { message: error.message }
      );
    }
  }
);

/**
 * Release held payment
 * POST /api/admin-karigar/payments/:paymentId/release
 */
export const releasePayment = createAsyncThunk(
  "karigar/payment/release",
  async (paymentId, { rejectWithValue }) => {
    try {
      const response = await api.post(`${BASE_URL}/${paymentId}/release`);
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data || { message: error.message }
      );
    }
  }
);

/**
 * Cancel payment (NEW)
 * POST /api/admin-karigar/payments/:paymentId/cancel
 *
 * Cancels a pending or on_hold payment:
 * - Changes status to 'cancelled'
 * - Decrements karigar's pendingPayments
 * - Removes payment reference from jobs
 */
export const cancelPayment = createAsyncThunk(
  "karigar/payment/cancel",
  async ({ paymentId, reason }, { rejectWithValue }) => {
    try {
      const response = await api.post(`${BASE_URL}/${paymentId}/cancel`, {
        reason,
      });
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data || { message: error.message }
      );
    }
  }
);

// ============================================================================
// BULK OPERATIONS
// ============================================================================

/**
 * Bulk process multiple payments
 * POST /api/admin-karigar/payments/bulk-process
 */
export const bulkProcessPayments = createAsyncThunk(
  "karigar/payment/bulkProcess",
  async ({ paymentIds, paymentMethod, notes }, { rejectWithValue }) => {
    try {
      const response = await api.post(`${BASE_URL}/bulk-process`, {
        paymentIds,
        paymentMethod,
        notes,
      });
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data || { message: error.message }
      );
    }
  }
);

// ============================================================================
// REPORTS & STATS
// ============================================================================

/**
 * Fetch payment dashboard statistics
 * GET /api/admin-karigar/payments/dashboard
 */
export const fetchPaymentStats = createAsyncThunk(
  "karigar/payment/fetchStats",
  async (params = {}, { rejectWithValue }) => {
    try {
      const response = await api.get(`${BASE_URL}/dashboard`, { params });
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data || { message: error.message }
      );
    }
  }
);

/**
 * Generate payment report
 * POST /api/admin-karigar/payments/report
 */
export const generatePaymentReport = createAsyncThunk(
  "karigar/payment/generateReport",
  async (reportParams, { rejectWithValue }) => {
    try {
      const response = await api.post(`${BASE_URL}/report`, reportParams);
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data || { message: error.message }
      );
    }
  }
);

/**
 * Fetch karigar earnings summary
 * GET /api/admin-karigar/payments/earnings/:karigarId
 */
export const fetchKarigarEarnings = createAsyncThunk(
  "karigar/payment/fetchEarnings",
  async ({ karigarId, params = {} }, { rejectWithValue }) => {
    try {
      const response = await api.get(`${BASE_URL}/earnings/${karigarId}`, {
        params,
      });
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data || { message: error.message }
      );
    }
  }
);

/**
 * Calculate payment preview for a job
 * GET /api/admin-karigar/payments/calculate/:jobId
 */
export const calculateJobPayment = createAsyncThunk(
  "karigar/payment/calculateJob",
  async (jobId, { rejectWithValue }) => {
    try {
      const response = await api.get(`${BASE_URL}/calculate/${jobId}`);
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data || { message: error.message }
      );
    }
  }
);

// ============================================================================
// EXPORT
// ============================================================================

/**
 * Export payments to CSV/JSON
 * POST /api/admin-karigar/payments/export
 */
export const exportPayments = createAsyncThunk(
  "karigar/payment/export",
  async (exportParams, { rejectWithValue }) => {
    try {
      const response = await api.post(`${BASE_URL}/export`, exportParams, {
        responseType: exportParams.format === "csv" ? "blob" : "json",
      });

      // If CSV format, trigger download
      if (exportParams.format === "csv" && response.data instanceof Blob) {
        const url = window.URL.createObjectURL(response.data);
        const a = document.createElement("a");
        a.href = url;
        a.download = `payments-export-${
          new Date().toISOString().split("T")[0]
        }.csv`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        window.URL.revokeObjectURL(url);
        return { success: true, message: "Export downloaded successfully" };
      }

      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data || { message: error.message }
      );
    }
  }
);

// ============================================================================
// LEGACY ALIASES (for backward compatibility)
// ============================================================================

// Alias for fetchPaymentStats
export const fetchPaymentDashboard = fetchPaymentStats;
